<script setup>
import { computed, ref, onMounted, nextTick, watch } from "vue";
import OrganisationSwitcher from "@/components/layout/OrganisationSwitcher.vue";
import { isMaster } from "@/stores/modules/organisations";
// import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import CustomIcon from "@/components/custom/CustomIcon.vue";
import CustomTabs from "@/components/custom/CustomTabs.vue";
import CustomToggle from "@/components/custom/CustomToggle.vue";
import { UserTypes } from "../../models/enums";
import Switch from "../utils/Switch.vue";
import ToggleButton from "@/volt/ToggleButton.vue";
// import ToggleSwitch from '@/volt/ToggleSwitch.vue';
import { useI18n } from "vue-i18n";
import { Button, SelectButton, Dialog, ToggleSwitch } from "primevue";
import { useLanguage } from "@/composables/useLanguage";

const { availableLocales, t } = useI18n();
const store = useStore();
const route = useRoute();
const router = useRouter();

const emit = defineEmits(["update:active"]);

const props = defineProps({
  showBackButton: {
    type: Boolean,
    default: false,
  },
  showAddButton: {
    type: Boolean,
    default: false,
  },
  position: {
    type: String,
    default: "top",
  },
});

const { selectedLanguage, changeLanguage } = useLanguage();

const active = ref(false);

const user = computed(() => store.getters["users/user"]);
const isOwner = computed(
  () => (user.value?.usertype ?? 0) >= UserTypes.President
);

// Owners get a link to their organisation labelled with its name, so pull the
// organisation document in once we know which account they belong to.
const organisationLabel = computed(
  () => store.getters["organisations/title"] || t("OrganisationSettings")
);

watch(
  [isOwner, () => user.value?.account],
  ([owner, account]) => {
    if (owner && account != null)
      store.dispatch("organisations/fetchByAccountId", account);
  },
  { immediate: true }
);
const isDarkMode = computed({
  get() {
    return store.getters.isDarkMode;
  },
  set(val) {
    store.dispatch("setDarkMode", { darkMode: val });
  },
});

const languageOptions = computed(() => {
  const result = availableLocales.map((el, index) => ({
    key: index,
    value: el == "da" ? "Dansk" : "English",
  }));
  // const result = availableLocales.map((el) => el)
  return result;
});

const onClickedOutside = async () => {
  active.value = false;
};

const goToPage = (value) => {
  router.push({ path: "/" + value });
  active.value = false;
};

const onLogOut = async () => {
  await store.dispatch("auth/logOut", { user: user.value });
  window.location.href = "/";
};

onMounted(async () => {
  if (localStorage.getItem("darkMode")) {
    if (localStorage.getItem("darkMode") == "true") {
      isDarkMode.value = true;
    }
  }

  // if(route.name == "event" || route.name == "home"){
  //   activePage.value = 'Events'
  // } else if(route.name == "profile"){
  //   activePage.value = 'Profile'
  // }

  // if(route.name == "home") {
  //   gsap.fromTo(header.value, {top:-100}, {top: 0, duration: 1, ease: CustomEase.create("custom", "M0,0 C0.095,0 0.168,0.108 0.2,0.2 0.233,0.299 0.294,0.616 0.4,0.8 0.49,0.956 0.704,1 1,1 ")})
  // }
});
</script>

<template>
  <div
    class="sm:hidden w-full h-full absolute top-0 left-0 pointer-events-none"
  >
    <div
      class="w-full fixed z-40"
      :class="{ 'bottom-0': position === 'bottom' }"
    >
      <div class="flex w-full justify-between p-4">
        <div class="left">
          <Button
            v-if="showBackButton"
            rounded
            class="!border !bg-white"
            variant="outlined"
            size="large"
            icon="pi pi-icon pi-arrow-left"
            @click="router.back()"
          />
        </div>
        <div
          class="right bg-white border rounded-full flex pointer-events-auto"
        >
          <Button
            v-if="showAddButton"
            class="!pr-2"
            rounded
            variant="text"
            size="large"
            icon="pi pi-icon pi-plus"
          />
          <Button
            rounded
            :class="{ '!pl-0': showAddButton }"
            severity="secondary"
            size="large"
            variant="text"
            icon="pi pi-icon pi-bars"
            @click="active = true"
          />
        </div>
      </div>
    </div>

    <Dialog
      v-model:visible="active"
      :modal="true"
      :dismissableMask="true"
      pt:root:class="!p-0 w-[calc(100%-32px)] !bg-white sm:w-auto max-w-sm !shadow-[0_20px_20px_-16px_rgba(0,0,0,0.3)]"
      pt:mask:class="backdrop-blur-sm !bg-transparent"
    >
      <template #container>
        <div class="mobile-navigation">
          <div
            class="option-page"
            :class="{ selected: route.name == 'home' || route.name == 'event' }"
            @click="goToPage('')"
          >
            <span class="label">{{ t("Calendar") }}</span>
          </div>
          <div
            class="option-page"
            :class="{
              selected: route.name == 'users' || route.name == 'users',
            }"
            @click="goToPage('users')"
          >
            <span class="label">{{ t("Users") }}</span>
          </div>
          <div
            class="option-page"
            :class="{
              selected:
                route.name == 'repertoire' || route.name == 'repertoire-song',
            }"
            @click="goToPage('repertoire')"
          >
            <span class="label">{{ t("Songs") }}</span>
          </div>
          <div
            class="option-page"
            :class="{ selected: route.name == 'profiler' }"
            @click="goToPage('profile')"
          >
            <span class="label">{{ t("Profile") }}</span>
          </div>
          <div
            v-if="user?.usertype > UserTypes.Admin"
            class="option-page"
            :class="{ selected: route.name == 'settings' }"
            @click="goToPage('settings')"
          >
            <span class="label">{{ t("Settings") }}</span>
          </div>
          <div
            v-if="isOwner"
            class="option-page"
            :class="{ selected: route.name == 'organisation' }"
            @click="goToPage('organisation')"
          >
            <span class="label">{{ organisationLabel }}</span>
          </div>
          <div
            v-if="user?.usertype >= UserTypes.Admin"
            class="option-page"
            :class="{ selected: route.name == 'schedule-templates' }"
            @click="goToPage('organisation/templates')"
          >
            <span class="label">{{ t("ScheduleTemplates") }}</span>
          </div>
          <div
            v-if="isMaster(user)"
            class="option-page"
            :class="{ selected: route.name == 'platform' }"
            @click="goToPage('platform')"
          >
            <span class="label">{{ t("PlatformOrganisations") }}</span>
          </div>

          <div class="quick-settings">
            <div v-if="user" class="setting">
              <OrganisationSwitcher />
            </div>
            <div class="setting">
              <SelectButton
                :modelValue="selectedLanguage"
                :options="languageOptions"
                optionLabel="value"
                optionValue="key"
                size="small"
                fluid
                @update:modelValue="changeLanguage"
              />
            </div>
            <div v-if="false" class="setting">
              <span>Dark mode</span><CustomToggle v-model="isDarkMode" />
            </div>
            <div v-if="user" class="setting user">
              <span class="text-sm"
                ><span class="block text-xs">{{ t("Account") }}:</span>
                {{ user.name.substring(0, user.name.indexOf(" ")) }}</span
              >
              <Button
                severity="secondary"
                size="small"
                :label="t('logOut')"
                @click="onLogOut"
              />
              <!-- <button class="secondary border-surface-500 !text-surface-800 dark:!text-neutral-200 !h-8" @click="logOutHit">Log out</button> -->
            </div>
          </div>
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style lang="scss" scoped>
.p-overlay-mask {
  background: none !important;
}

.wrapper {
  @apply absolute flex items-center justify-center w-full h-full top-0 left-0;
}
.background {
  @apply absolute top-0 left-0 w-full h-full z-50 dark:bg-black/50;
}
.mobile-navigation {
  @apply dark:text-neutral-300 pt-4 overflow-hidden z-50 w-full;
  div.option-page {
    @apply relative flex items-center px-6 cursor-pointer;
    .label {
      @apply relative;
      @apply text-neutral-500 text-xl leading-10;
    }
    &.selected {
      @apply relative;
      .label::after {
        @apply content-[''] absolute left-0 bottom-2 w-full h-[2px] bg-primary-500;
      }
      .label {
        @apply text-black z-20;
      }
    }
    &:last-child {
      @apply border-0;
    }
  }
  div.quick-settings {
    @apply flex flex-col z-50 text-surface-800 bg-white dark:bg-surface-800 m-3 py-2 px-3 rounded-lg;
    .setting {
      @apply flex justify-between items-center;
      &.user {
        @apply border-t dark:border-t-surface-600 mt-2 pt-2;
      }
    }
  }
}
</style>
